import * as React from "react"
import {useCallback, useEffect, useMemo, useState} from "react"
import {CardSeeker} from "@ui-seeker/core";
import {Box, Button, Grid, Typography} from "@mui/material";

import {LabelCountry} from "@components";
import {MatchersLoadingSkuBlock} from "../../@components";
import {productsMatcherGroupedProductIDs} from "../@signals";
import {useProductsMatcherBrandSkus} from "../@hooks";
import ProductOption from "./ProductOption";
import ProductsMatcherOptionsFilters from "./Filters";
import ProductsMatcherImageComparisonModal from "./ImageComparisonModal";

const VISIBLE_INCREMENT = 30

function groupOptionsByField(allOptions, field) {
	const groups = {}
	for (const opt of allOptions) {
		const key = opt[field] || ''
		if (!key) continue // Skip items without the grouping field
		if (!groups[key]) groups[key] = []
		groups[key].push(opt)
	}

	return Object.values(groups).map(items => {
		// Pick the item with the highest AI score as representative
		const sorted = [...items].sort((a, b) => {
			const distA = parseFloat(a.AIDistance) || 0
			const distB = parseFloat(b.AIDistance) || 0
			return distB - distA
		})

		const representative = sorted[0]
		const bestAIDistance = sorted[0].AIDistance
		const isIAOption = items.some(i => i._isIAOption)
		const productIDs = items.map(i => i.productID)

		return {
			...representative,
			AIDistance: bestAIDistance,
			_isIAOption: isIAOption,
			_groupCount: items.length,
			_groupProductIDs: productIDs
		}
	})
}

export default function ProductsMatcherOptions({ showOptions, showMappedProducts=true, targetIndex, skuID, skuName, subjectImage, brandID, siteID, retailer, country, options=[], selected=null, setSelectedSKU, saveMapping, groupBy=null }) {
	const { brandOptions, allBrandOptions, isLoading, searchText, handleChangeSearchText } = useProductsMatcherBrandSkus(brandID, siteID, options.map(({productID}) => productID))
	const [visibleCount, setVisibleCount] = useState(VISIBLE_INCREMENT)

	const filteredBrandOptions = useMemo(() => {
		return brandOptions.filter(data => showMappedProducts || !data.alreadyMapped)
	}, [brandOptions, showMappedProducts])

	const visibleBrandOptions = useMemo(() => {
		return filteredBrandOptions.slice(0, visibleCount)
	}, [filteredBrandOptions, visibleCount])

	const hasMoreToShow = filteredBrandOptions.length > visibleCount
	const remainingCount = filteredBrandOptions.length - visibleCount

	// Grouped options: merge AI scoring into brand data, group by field
	const groupedOptions = useMemo(() => {
		if (!groupBy) return null

		// Build AI scoring lookup by productID
		const aiScoreMap = new Map()
		if (showOptions && !searchText) {
			for (const opt of options) {
				aiScoreMap.set(opt.productID, opt.AIDistance)
			}
		}

		// Use ALL brand options (unfiltered) so AI representative is included with its reference
		const allBrands = showMappedProducts ? allBrandOptions : allBrandOptions.filter(d => !d.alreadyMapped)

		const allOptions = []
		const seen = new Set()

		// Add brand options, merging AI scoring when productID matches
		for (const opt of allBrands) {
			const aiDistance = aiScoreMap.get(opt.productID)
			allOptions.push({
				...opt,
				_isIAOption: !!aiDistance,
				AIDistance: aiDistance || opt.AIDistance
			})
			seen.add(opt.productID)
		}

		// Add AI options NOT in brand (standalone — may lack reference)
		if (showOptions && !searchText) {
			for (const opt of options) {
				if (!seen.has(opt.productID)) {
					allOptions.push({ ...opt, _isIAOption: true })
					seen.add(opt.productID)
				}
			}
		}

		// Items without the grouping field value are shown ungrouped
		const withField = allOptions.filter(opt => opt[groupBy])
		const withoutField = allOptions.filter(opt => !opt[groupBy])

		const grouped = groupOptionsByField(withField, groupBy)

		// Add ungrouped AI candidates alongside grouped items (non-AI ungrouped go at end)
		const ungroupedRest = []
		for (const opt of withoutField) {
			const entry = { ...opt, _groupCount: 1, _groupProductIDs: [opt.productID] }
			if (opt._isIAOption) grouped.push(entry)
			else ungroupedRest.push(entry)
		}

		// Single sort: all AI-scored items (grouped + ungrouped) by score descending
		grouped.sort((a, b) => {
			const distA = parseFloat(a.AIDistance) || 0
			const distB = parseFloat(b.AIDistance) || 0
			return distB - distA
		})

		grouped.push(...ungroupedRest)

		return grouped
	}, [groupBy, options, allBrandOptions, showOptions, searchText, showMappedProducts])

	const visibleGroupedOptions = useMemo(() => {
		if (!groupedOptions) return []
		return groupedOptions.slice(0, visibleCount)
	}, [groupedOptions, visibleCount])

	const hasMoreGrouped = groupedOptions ? groupedOptions.length > visibleCount : false
	const remainingGrouped = groupedOptions ? groupedOptions.length - visibleCount : 0

	// Wrap setSelectedSKU to manage grouped product IDs
	const handleSelectSKU = useCallback((productID, groupProductIDs) => {
		setSelectedSKU(productID)
		productsMatcherGroupedProductIDs.value = productID && groupProductIDs
			? groupProductIDs.filter(id => id !== productID)
			: []
	}, [setSelectedSKU])

	useEffect(() => {
		handleChangeSearchText('')
		setVisibleCount(VISIBLE_INCREMENT)
		productsMatcherGroupedProductIDs.value = []
	}, [targetIndex]);

	useEffect(() => {
		setVisibleCount(VISIBLE_INCREMENT)
	}, [searchText])

	// Clear grouped IDs when groupBy changes
	useEffect(() => {
		productsMatcherGroupedProductIDs.value = []
	}, [groupBy])

	return <CardSeeker>
		<Grid container spacing={4}>
			<Grid item xs={2}>
				<LabelCountry country={country} label={retailer} />
			</Grid>

			<Grid item xs={10}>
				<ProductsMatcherOptionsFilters
					searchText={searchText}
					onInputChange={handleChangeSearchText}
				/>
			</Grid>

			{ !!isLoading && <Grid item xs={12}>
				<MatchersLoadingSkuBlock />
			</Grid> }

			<Grid item xs={12}>
				<Grid container spacing={4} maxHeight="70vh" sx={{ overflowY: "auto" }}>
					{/* Grouped view */}
					{ !isLoading && groupBy && visibleGroupedOptions.map((data, idx) => <ProductOption key={data.productID}
							isIAOption={data._isIAOption}
							skuID={skuID}
							skuName={skuName}
							subjectImage={subjectImage}
							productID={data.productID}
							isSelected={selected !== null && selected === data.productID}
							data={data}
							setSelectedSKU={handleSelectSKU}
							brandOptionsIndex={idx}
							groupCount={data._groupCount}
							groupProductIDs={data._groupProductIDs}
						/>) }

					{/* Ungrouped view: AI options */}
					{ !isLoading && !groupBy && !searchText && options.filter(op => showOptions || op.productID === selected).map((data, idx) => <ProductOption key={data.productID}
							isIAOption
							skuID={skuID}
							skuName={skuName}
							subjectImage={subjectImage}
							productID={data.productID}
							isSelected={selected !== null && selected === data.productID}
							data={data}
							setSelectedSKU={handleSelectSKU}
							brandOptionsIndex={idx}
						/>) }

					{/* Ungrouped view: Brand options */}
					{ !isLoading && !groupBy && visibleBrandOptions.map((data, idx) => {
						const aiOptionData = options.find(op => op.productID === data.productID)
						const fullArray = showOptions && !searchText ? [...options, ...brandOptions] : brandOptions
						const modalIndex = fullArray.findIndex(item => item.productID === data.productID)

						return <ProductOption key={data.productID}
								isIAOption={!!aiOptionData}
								skuID={skuID}
								skuName={skuName}
							subjectImage={subjectImage}
								productID={data.productID}
								isSelected={selected !== null && selected === data.productID}
								data={data}
								setSelectedSKU={handleSelectSKU}
								brandOptionsIndex={modalIndex >= 0 ? modalIndex : idx}
							/>
					}) }

					{/* Load more for ungrouped */}
					{ !isLoading && !groupBy && hasMoreToShow && <Grid item xs={12}>
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
							<Button
								variant="outlined"
								size="small"
								onClick={() => setVisibleCount(prev => prev + VISIBLE_INCREMENT)}
							>
								Load more ({remainingCount} remaining)
							</Button>
						</Box>
					</Grid> }

					{/* Load more for grouped */}
					{ !isLoading && groupBy && hasMoreGrouped && <Grid item xs={12}>
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
							<Button
								variant="outlined"
								size="small"
								onClick={() => setVisibleCount(prev => prev + VISIBLE_INCREMENT)}
							>
								Load more ({remainingGrouped} remaining)
							</Button>
						</Box>
					</Grid> }

					{ !isLoading && !groupBy && filteredBrandOptions.length > 0 && <Grid item xs={12}>
						<Typography variant="caption" color="text.secondary" align="center" display="block">
							Showing {Math.min(visibleCount, filteredBrandOptions.length)} of {filteredBrandOptions.length} products
						</Typography>
					</Grid> }

					{ !isLoading && groupBy && groupedOptions && <Grid item xs={12}>
						<Typography variant="caption" color="text.secondary" align="center" display="block">
							Showing {Math.min(visibleCount, groupedOptions.length)} of {groupedOptions.length} groups
						</Typography>
					</Grid> }
				</Grid>
			</Grid>
		</Grid>

		<ProductsMatcherImageComparisonModal
			brandOptions={groupBy && groupedOptions ? groupedOptions : (showOptions && !searchText ? [...options, ...(brandOptions ?? [])] : brandOptions)}
			setSelectedSKU={handleSelectSKU}
			saveMapping={saveMapping}
		/>
	</CardSeeker>
}